import { useCallback } from "react";
import { useSelector } from "react-redux";
import { AuthPermissionAction } from "../features/auth/@types";
import { selectUserPermissions } from "../features/auth/authSlice";

export const usePermissions = () => {
  const permissions = useSelector(selectUserPermissions);

  const hasPermission = useCallback(
    (resourceName: string, action?: AuthPermissionAction): boolean => {
      if (!permissions.length) return false;

      const permission = permissions.find(
        (p) => p.resourceName.toLowerCase() === resourceName.toLowerCase()
      );

      if (!permission) return false;
      if (!action) return true;

      return permission.actions.includes(action);
    },
    [permissions]
  );

  const canView = useCallback(
    (resource: string) => hasPermission(resource, "View"),
    [hasPermission]
  );
  const canCreate = (resource: string) => hasPermission(resource, "Create");
  const canUpdate = (resource: string) => hasPermission(resource, "Update");
  const canDelete = (resource: string) => hasPermission(resource, "Delete");
  const canExport = (resource: string) => hasPermission(resource, "Export");
  const canSearch = (resource: string) => hasPermission(resource, "Search");
  const canApprove = (resource: string) => hasPermission(resource, "Approve");
  const canDecline = (resource: string) => hasPermission(resource, "Decline");

  const getResourcePermissions = (resource: string) => {
    const permission = permissions.find(
      (p) => p.resourceName.toLowerCase() === resource.toLowerCase()
    );
    return permission?.actions || [];
  };

  return {
    permissions,
    hasPermission,
    canView,
    canCreate,
    canUpdate,
    canDelete,
    canExport,
    canSearch,
    canApprove,
    canDecline,
    getResourcePermissions,
  };
};
